import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { QRCodeSVG } from "qrcode.react";
import { api } from "@/lib/api";
import { useStats } from "@/lib/useStats";
import usePageTitle from "@/hooks/usePageTitle";

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const PDF_URL = `${BACKEND}/api/about/pdf`;
const PARTNERSHIP_TYPES = [
  "شراكة مؤسّسيّة تعليميّة",
  "تكامل مع منهج جامعيّ",
  "احتضان وتمويل",
  "بحث وتطوير",
  "ترخيص محتوى",
  "أخرى",
];

const GALLERY = [
  { src: "/screenshots/01-home.webp", title: "الصفحة الرئيسيّة",
    desc: "رحلة المنهج كاملةً مع شريط التقدّم الكلّي." },
  { src: "/screenshots/02-curriculum.webp", title: "صفحة المنهج",
    desc: "مسارات المهارات والشارات وشريط الإنجاز." },
  { src: "/screenshots/03-lesson.webp", title: "صفحة الدرس",
    desc: "قالب درس من 10 مراحل تفاعليّة متدرّجة." },
  { src: "/screenshots/04-chat.webp", title: "المعلّم الذكيّ",
    desc: "محادثة بالعربيّة الفصيحة مع تقييمٍ مباشر." },
  { src: "/screenshots/05-profile.webp", title: "الملفّ الشخصيّ",
    desc: "إحصاءاتٌ وشاراتٌ وروابط سريعة." },
];

const ROADMAP = [
  { phase: "المرحلة 1 — أُنجزت", title: "إصدار MVP", status: "done",
    desc: "منهجٌ تفاعليٌّ متكامل، معلّم ذكاء اصطناعيّ، PWA، صفحات قانونيّة كاملة." },
  { phase: "المرحلة 2 — جارية", title: "توسعة المحتوى التعليميّ", status: "active",
    desc: "إضافة دروس متقدّمة (الجناس، السجع، الطباق، البديع)، ونصوصٍ تراثيّة." },
  { phase: "المرحلة 3", title: "لوحة المعلّم والمؤسّسة", status: "next",
    desc: "تتبّع تقدّم الطلّاب جماعيًّا، إحصاءات، تقارير دوريّة للمدارس." },
  { phase: "المرحلة 4", title: "إثراء صوتيّ ومسموع", status: "next",
    desc: "قراءاتٌ صوتيّة بأصواتٍ عربيّة فصيحة، مع تمرين الإلقاء." },
  { phase: "المرحلة 5", title: "الشراكات والنشر المؤسّسيّ", status: "next",
    desc: "ربط التطبيق ببرامج وزارات التعليم والمجامع اللغوية في العالم العربيّ." },
];

const FEATURES = [
  { ic: "📖", t: "قالب درس موحّد", d: "10 مراحل تشمل المفردات، الشرح، الأمثلة، التقييم، الاختبار." },
  { ic: "🧠", t: "معلّم ذكاء اصطناعيّ", d: "يُحاور بالعربيّة الفصيحة، ويُقيّم الكتابات بمعايير بلاغيّة." },
  { ic: "🔓", t: "منهج تدرّجيّ", d: "قفلٌ تدرّجيّ يضمن بناء المهارة طبقةً فوق طبقة." },
  { ic: "🏆", t: "لعبنة كاملة", d: "نقاط XP، سلسلة يوميّة، شاراتٌ للدروس وأخرى تراكميّة." },
  { ic: "📱", t: "PWA قابل للتثبيت", d: "يعمل على الجوّال والحاسب، ويُثبَّت كتطبيق مستقل." },
  { ic: "🌐", t: "تجربة عربيّة كاملة", d: "تصميم RTL راقٍ بخطوط Amiri وReem Kufi." },
  { ic: "🔐", t: "تسجيل جوجل + وضع زائر", d: "ابدأ زائرًا، وتقدّمك يُدمج عند تسجيل الدخول." },
  { ic: "📊", t: "تتبّع تفصيليّ", d: "إحصاءات للدروس والتحدّيات والسلسلة، وملفٌّ شخصيّ كامل." },
];

// --------- Reusable Section wrapper ---------
function Section({ id, icon, title, children, testid }) {
  return (
    <section id={id} className="about-section" data-testid={testid || `about-section-${id}`}>
      <div className="about-section-header">
        <span className="about-icon" aria-hidden>{icon}</span>
        <h2>{title}</h2>
      </div>
      <div className="about-section-body">{children}</div>
    </section>
  );
}

// --------- Partnership modal ---------
function PartnershipModal({ open, onClose }) {
  const [form, setForm] = useState({
    name: "", organization: "", email: "", phone: "",
    partnership_type: PARTNERSHIP_TYPES[0], message: "",
  });
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const [err, setErr] = useState("");

  if (!open) return null;
  const upd = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    if (form.name.length < 2 || form.email.length < 4 || form.message.length < 10) {
      setErr("يُرجى تعبئة الاسم والبريد والرسالة بشكلٍ صحيح.");
      return;
    }
    setBusy(true);
    try {
      const res = await fetch(`${BACKEND}/api/partnership/request`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(form),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || "فشل الإرسال");
      setDone(true);
    } catch (e) {
      setErr(e.message || "تعذّر إرسال الطلب.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="partnership-modal-backdrop" onClick={onClose}
         data-testid="partnership-modal-backdrop">
      <div className="partnership-modal" onClick={(e) => e.stopPropagation()}
           data-testid="partnership-modal" role="dialog" aria-modal="true">
        <button className="partnership-modal-close" onClick={onClose}
                data-testid="partnership-modal-close" aria-label="إغلاق">×</button>

        {done ? (
          <div className="partnership-success" data-testid="partnership-success">
            <div className="success-mark">✓</div>
            <h3>تمّ إرسال طلبكم بنجاح</h3>
            <p>
              شكرًا لاهتمامكم بمشروع بَلاغة. سنتواصل معكم في أقرب وقتٍ ممكن
              على البريد <strong dir="ltr">{form.email}</strong>.
            </p>
            <button className="btn btn-primary" onClick={onClose}
                    data-testid="partnership-success-close">حسنًا</button>
          </div>
        ) : (
          <>
            <h3>طلب شراكة أو تعاون</h3>
            <p className="dim">
              أخبرنا بهدف الشراكة وسنعاود التواصل معكم خلال 48 ساعة.
            </p>
            <form onSubmit={submit} className="partnership-form" noValidate>
              <label>
                <span>الاسم الكامل *</span>
                <input value={form.name} onChange={upd("name")}
                       data-testid="partnership-name" required minLength={2} />
              </label>
              <label>
                <span>المؤسّسة / الجهة</span>
                <input value={form.organization} onChange={upd("organization")}
                       data-testid="partnership-org"
                       placeholder="جامعة / مدرسة / حاضنة..." />
              </label>
              <div className="form-row">
                <label>
                  <span>البريد الإلكترونيّ *</span>
                  <input type="email" value={form.email} onChange={upd("email")}
                         data-testid="partnership-email" required dir="ltr" />
                </label>
                <label>
                  <span>الهاتف (اختياريّ)</span>
                  <input value={form.phone} onChange={upd("phone")}
                         data-testid="partnership-phone" dir="ltr" />
                </label>
              </div>
              <label>
                <span>نوع الشراكة</span>
                <select value={form.partnership_type}
                        onChange={upd("partnership_type")}
                        data-testid="partnership-type">
                  {PARTNERSHIP_TYPES.map((t) => <option key={t}>{t}</option>)}
                </select>
              </label>
              <label>
                <span>رسالتكم *</span>
                <textarea rows={5} value={form.message}
                          onChange={upd("message")}
                          data-testid="partnership-message"
                          required minLength={10}
                          placeholder="فكرة الشراكة، الجمهور المستهدف، النطاق الزمنيّ..." />
              </label>
              {err && <div className="partnership-error" data-testid="partnership-error">{err}</div>}
              <div className="partnership-actions">
                <button type="submit" className="btn btn-primary"
                        disabled={busy} data-testid="partnership-submit">
                  {busy ? "...جارٍ الإرسال" : "إرسال الطلب"}
                </button>
                <button type="button" className="btn btn-ghost" onClick={onClose}>
                  إلغاء
                </button>
              </div>
            </form>
          </>
        )}
      </div>
    </div>
  );
}

// --------- QR section (reused from prior iteration) ---------
function QRSection() {
  // Canonical public URL — used for QR codes even on preview/staging so users
  // scanning always reach the production site.
  const appUrl = "https://www.balagha.tech";
  const copy = async () => {
    try { await navigator.clipboard.writeText(appUrl); alert("تمّ نسخ الرابط ✓"); }
    catch { /* ignore clipboard errors */ }
  };
  return (
    <section className="about-section about-qr" data-testid="about-section-qr">
      <div className="about-section-header">
        <span className="about-icon" aria-hidden>📲</span>
        <h2>جرّب التطبيق الآن — امسح الرمز</h2>
      </div>
      <div className="about-qr-body">
        <div className="about-qr-card" data-testid="about-qr-card">
          <div className="about-qr-frame">
            <QRCodeSVG
              value={appUrl} size={200} level="H" bgColor="#ffffff" fgColor="#1a1438"
              marginSize={2}
              imageSettings={{
                src: "data:image/svg+xml;utf8," + encodeURIComponent(
                  '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 64 64"><rect width="64" height="64" rx="14" fill="#f5c87a"/><text x="50%" y="58%" font-family="Reem Kufi, Amiri, serif" font-size="38" font-weight="700" text-anchor="middle" fill="#1a1438">ب</text></svg>'),
                height: 36, width: 36, excavate: true,
              }}
            />
          </div>
          <div className="about-qr-cap">بَلاغة · معلّم البلاغة الذكيّ</div>
        </div>
        <div className="about-qr-info">
          <p>
            وجّه كاميرا الجوّال نحو الرمز لتبدأ التجربة فورًا. يعمل التطبيق
            على الجوّال والحاسب، ويُمكن تثبيته كتطبيقٍ مستقلّ.
          </p>
          <div className="about-qr-url" data-testid="about-qr-url" dir="ltr">{appUrl}</div>
          <div className="about-qr-actions">
            <button className="btn btn-gold" onClick={copy} data-testid="about-qr-copy">
              📋 نسخ الرابط
            </button>
            <a className="btn btn-ghost" href={appUrl} target="_blank" rel="noreferrer"
               data-testid="about-qr-open">🔗 فتح التطبيق</a>
          </div>
        </div>
      </div>
    </section>
  );
}

// =========================================================================
export default function AboutPage() {
  const navigate = useNavigate();
  usePageTitle("عن بَلاغة · القصّة والمنهج والشراكات");
  const stats = useStats();
  const [partnerOpen, setPartnerOpen] = useState(false);
  const [downloading, setDownloading] = useState(false);

  const downloadPDF = () => {
    // Open in a new tab so the user can return to the app easily on mobile.
    // Note: avoid `<a download>` + `target=_blank` combo — mobile Safari
    // ignores `download` and replaces the current tab. window.open is the
    // most reliable cross-platform behaviour.
    setDownloading(true);
    try {
      const w = window.open(PDF_URL, "_blank", "noopener,noreferrer");
      if (!w) {
        // Popup blocked → fallback to a one-click link
        const a = document.createElement("a");
        a.href = PDF_URL;
        a.target = "_blank";
        a.rel = "noopener noreferrer";
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
      }
    } finally {
      setTimeout(() => setDownloading(false), 1000);
    }
  };

  return (
    <div className="shell has-bottom-nav about-page" data-testid="about-page">
      {/* ==================== HERO ==================== */}
      <div className="page-header about-hero">
        <div className="about-hero-mark">ب</div>
        <h1 data-testid="about-title">
          ملفّ مشروع <span className="gold">بَلاغة</span>
        </h1>
        <p>
          منصّة عربيّة ذكيّة لإحياء البلاغة العربيّة، تجمع أصالة التراث
          ودقّة الذكاء الاصطناعيّ. هذه الصفحة العامّة مُعدّةٌ خصّيصًا للجهات
          التعليميّة والداعمين.
        </p>
        <div className="about-tagline">
          مُقدَّمٌ بفخرٍ للمؤسّسات التعليميّة، وعلى رأسها{" "}
          <strong>مجمع الملك سلمان العالميّ للغة العربيّة</strong>.
        </div>

        {/* TWO PROMINENT CTAs */}
        <div className="about-primary-ctas">
          <button
            className="btn btn-gold cta-primary"
            onClick={downloadPDF}
            data-testid="download-pdf-btn"
            disabled={downloading}
          >
            <span aria-hidden>📄</span>
            {downloading ? "...جارٍ التحضير" : "تحميل ملف المشروع (PDF)"}
          </button>
          <button
            className="btn btn-primary cta-primary"
            onClick={() => setPartnerOpen(true)}
            data-testid="request-partnership-btn"
          >
            <span aria-hidden>🤝</span>
            طلب شراكة
          </button>
        </div>
      </div>

      {/* ==================== 1. PROJECT STORY ==================== */}
      <Section id="story" icon="📜" title="قصة المشروع">
        <p>
          وُلد مشروع «بَلاغة» من رحم تجربةٍ حيّة مع طلّاب اللغة العربيّة الذين
          وجدوا في علم البلاغة بحرًا من الجمال يصعب الإبحار فيه بأدواتٍ
          تقليديّة. كانت الفكرة: ماذا لو وُضع هذا العلم العريق في قالبٍ
          تفاعليٍّ ذكيّ يُحاكي طريقة المعلّم الحاذق؟
        </p>
        <p>
          بدأت الرحلة بتصميم قالب درسٍ موحّد قابل للتوسعة، ثمّ تطوّرت إلى
          منصّةٍ متكاملة تجمع المفردات والشرح والأمثلة والتمارين والتقييم
          الذكيّ، حتّى وصلنا إلى ما هو بين أيديكم اليوم: <strong>نسخة MVP
          مكتملة</strong> جاهزة للعرض المؤسّسي.
        </p>
      </Section>

      {/* ==================== 2. VISION + 3. MISSION ==================== */}
      <Section id="vision" icon="🌙" title="الرؤية والرسالة">
        <div className="about-mini-grid">
          <div>
            <h4>الرؤية</h4>
            <p>
              أن نُعيد للبلاغة العربيّة حضورها في وجدان الجيل الرقميّ، عبر
              منصّةٍ ذكيّة تجمع جمال اللغة وفصاحتها مع التجربة التعليميّة
              التفاعليّة الحديثة.
            </p>
          </div>
          <div>
            <h4>الرسالة</h4>
            <p>
              تمكين كلّ متعلّمٍ عربيّ — تلميذًا كان أم كاتبًا — من إتقان
              أساليب البلاغة، وتنمية ذوقه اللغويّ، بأدواتٍ تفاعليّةٍ مدعومةٍ
              بمعلّمٍ ذكاءٍ اصطناعيٍّ يُحاوره ويُقيّم كتاباته.
            </p>
          </div>
        </div>
      </Section>

      {/* ==================== 3.5 GOALS ==================== */}
      <Section id="goals" icon="🎯" title="أهداف المنصّة">
        <ul className="meth-outcomes" style={{ marginTop: 4 }}>
          <li>إحياءُ علم البلاغة العربيّة وتقديمه بأسلوبٍ تفاعليٍّ يُحاكي طبيعة الجيل الرقميّ.</li>
          <li>توفيرُ منهجٍ بنائيٍّ متكامل من المبتدئ إلى الاحترافيّ في {stats.tracks} مسارات.</li>
          <li>تمكينُ المتعلِّم من إنتاج نصوصٍ بلاغيّةٍ يتلقّى تقييمها بالذكاء الاصطناعيّ.</li>
          <li>منحُ شهاداتٍ موثّقةٍ برقم تحقّقٍ فريد تُضاف إلى السيرة العلميّة للمتعلِّم.</li>
          <li>تَشكيلُ شراكاتٍ مؤسّسيّةٍ مع الجامعات والمدارس والمراكز اللغويّة.</li>
          <li>الإسهامُ في الحفاظ على اللغة العربيّة بأدواتٍ معاصرة وتقنيّةٍ متقدّمة.</li>
        </ul>
      </Section>

      {/* ==================== 3.7 TARGET AUDIENCE ==================== */}
      <Section id="audience" icon="👥" title="الفئة المستهدفة">
        <div className="about-mini-grid">
          <div>
            <h4>الأفراد</h4>
            <p>
              <strong>طلّاب اللغة العربيّة</strong> في المراحل الإعداديّة والثانويّة
              والجامعيّة · <strong>الكُتّاب والمحرّرون</strong> الذين يَسعون إلى صقل
              أسلوبهم · <strong>الخطباء والمدرّبون</strong> ومُدوّنو المحتوى العربيّ ·
              <strong> هواة اللغة</strong> العشّاق للذوق الأدبيّ الراقي.
            </p>
          </div>
          <div>
            <h4>المؤسّسات</h4>
            <p>
              <strong>المدارس والجامعات</strong> الراغبة في إدماج البلاغة الذكيّة
              ضمن منهجها · <strong>المراكز اللغويّة</strong> ودور التَدريب ·
              <strong> المؤسّسات الإعلاميّة والنَّشريّة</strong> · <strong>الجمعيّات
              الثقافيّة</strong> ومُبادرات صون اللغة العربيّة.
            </p>
          </div>
        </div>
      </Section>

      {/* ==================== 4. EDUCATIONAL METHODOLOGY ==================== */}
      <Section id="methodology" icon="📚" title="المنهج التعليميّ">
        <p>
          يعتمد بَلاغة منهجيّة <strong>التعلّم البنائيّ التدرّجيّ</strong> التي
          تنقل المتعلّم من المفهوم البسيط إلى التطبيق المركّب، عبر عشر
          مراحل في كلّ درس:
        </p>
        <ol className="methodology-list">
          <li><span>1</span> المقدّمة وتأطير السياق</li>
          <li><span>2</span> المفردات وتأسيس المعجم البلاغيّ</li>
          <li><span>3</span> الشرح وتفصيل المفاهيم</li>
          <li><span>4</span> الأمثلة من القرآن والشعر</li>
          <li><span>5</span> أنشطة تفاعليّة (MCQ، مطابقة، ترتيب، تعبئة)</li>
          <li><span>6</span> تمرين موجَّه</li>
          <li><span>7</span> محادثة مع الذكاء الاصطناعيّ</li>
          <li><span>8</span> مهمّة كتابة يُقيّمها AI</li>
          <li><span>9</span> اختبار نهائيّ</li>
          <li><span>10</span> ملخّص ومراجعة</li>
        </ol>
        <p>
          ويُتوّج المتعلّم بشارةٍ مميّزة لكلّ درس، إضافةً إلى شاراتٍ تراكميّة
          (10، 25، 50، {stats.lessons} درسًا) مع الحفاظ على سلسلةٍ يوميّة تُحفّز الالتزام.
        </p>
      </Section>

      {/* ==================== 5. FEATURES ==================== */}
      <Section id="features" icon="⚙️" title="المزايا الحاليّة وقدرات المنصّة">
        <div className="about-stats" style={{ marginBottom: 18 }}>
          <div className="about-stat"><div className="num gold">{stats.lessons}</div><div>درسًا</div></div>
          <div className="about-stat"><div className="num gold">{stats.tests}</div><div>اختبارًا</div></div>
          <div className="about-stat"><div className="num gold">{stats.certificates}</div><div>شهادات</div></div>
          <div className="about-stat"><div className="num gold">∞</div><div>تمارين AI</div></div>
        </div>
        <div className="about-feat-grid">
          {FEATURES.map((f) => (
            <div className="about-feat" key={f.t}>
              <div className="ic">{f.ic}</div>
              <h4>{f.t}</h4>
              <p>{f.d}</p>
            </div>
          ))}
        </div>
      </Section>

      {/* ==================== 6. SCREENSHOT GALLERY ==================== */}
      <Section id="gallery" icon="🖼" title="لقطات من المنصّة" testid="about-section-gallery">
        <p style={{ marginBottom: 16 }}>
          لقطاتٌ مختارة من واجهات بَلاغة تُجسّد التجربة التعليميّة:
        </p>
        <div className="screenshot-gallery" data-testid="screenshot-gallery">
          {GALLERY.map((g, i) => (
            <figure key={g.src} className="shot" data-testid={`shot-${i}`}>
              <img src={g.src} alt={g.title} loading="lazy" />
              <figcaption>
                <strong>{g.title}</strong>
                <span>{g.desc}</span>
              </figcaption>
            </figure>
          ))}
        </div>
      </Section>

      {/* ==================== 7. CURRENT DEVELOPMENT STAGE ==================== */}
      <Section id="stage" icon="✅" title="المرحلة الحاليّة من التطوير">
        <div className="stage-banner" data-testid="stage-banner">
          <div className="stage-pulse" />
          <div>
            <h4>نسخة MVP — جاهزة للعرض المؤسّسي</h4>
            <p>
              يعمل التطبيق بكامل وظائفه على الإنترنت بنسبة استقرار <strong>100%</strong>،
              مع تغطية اختبار آليّ شاملة (61+ اختبار ناجح). جاهز للتجريب
              من قبل الجهات التعليميّة والشركاء.
            </p>
          </div>
        </div>
      </Section>

      {/* ==================== 8. FUTURE ROADMAP ==================== */}
      <Section id="roadmap" icon="🚀" title="خطة التوسّع المستقبليّة">
        <div className="about-roadmap">
          {ROADMAP.map((r, i) => (
            <div key={i} className={`rm-item rm-${r.status}`} data-testid={`rm-${i}`}>
              <span className="rm-phase">{r.phase}</span>
              <h4>{r.title}</h4>
              <p>{r.desc}</p>
            </div>
          ))}
        </div>
      </Section>

      {/* ==================== 9. FOUNDER ==================== */}
      <Section id="founder" icon="👤" title="المؤسّس">
        <div className="founder-card" data-testid="founder-card">
          <div className="founder-avatar" aria-hidden>س</div>
          <div className="founder-body">
            <h4>سامي الجابري</h4>
            <div className="founder-title">مؤسّس مشروع بَلاغة</div>
            <p>
              شغوفٌ بتعليم اللغة العربيّة وتسخير التقنيات الحديثة لخدمتها،
              ويسعى إلى ابتكار أدواتٍ تربويّة تُعيد للفصاحة مكانتها في الجيل
              الرقميّ. يجمع بين التخصّص اللغويّ والإصرار على جودة المنتج
              التعليميّ.
            </p>
            <a className="legal-link" dir="ltr"
               href="mailto:info@balagha.tech" data-testid="founder-email">
              info@balagha.tech
            </a>
          </div>
        </div>
      </Section>

      {/* ==================== 10. PARTNERSHIP ==================== */}
      <Section id="partnership" icon="🤝" title="فرص الشراكة والتعاون">
        <p>
          نُرحّب بالشراكات مع المؤسّسات التعليميّة، والمجامع اللغوية،
          والحاضنات، والداعمين الراغبين في إثراء لغة الضاد:
        </p>
        <ul className="about-list">
          <li><strong>الشراكة الأكاديميّة:</strong> دمج بَلاغة في مناهج المدارس والجامعات.</li>
          <li><strong>الشراكة المؤسّسيّة:</strong> ترخيص للمدارس والمراكز الثقافيّة.</li>
          <li><strong>التمويل والاحتضان:</strong> دعم التوسّع في المحتوى والتقنيات.</li>
          <li><strong>البحث والتطوير:</strong> دراساتٌ تطبيقيّة على التعلّم بالذكاء الاصطناعيّ.</li>
          <li><strong>الترويج والنشر:</strong> توصيل التطبيق إلى الجيل الرقميّ.</li>
        </ul>
        <div style={{ marginTop: 18, textAlign: "center" }}>
          <button
            className="btn btn-primary cta-primary"
            onClick={() => setPartnerOpen(true)}
            data-testid="partnership-inline-btn"
            style={{ padding: "12px 30px" }}
          >
            🤝 ابدأ محادثة شراكة
          </button>
        </div>
      </Section>

      {/* ==================== QR CODE ==================== */}
      <QRSection />

      {/* ==================== 11. CONTACT ==================== */}
      <Section id="contact" icon="📮" title="معلومات التواصل" testid="about-section-contact">
        <div className="about-contact-grid">
          <a className="about-contact-card" href="mailto:info@balagha.tech"
             data-testid="contact-email">
            <div className="ic">✉️</div>
            <div>
              <div className="lbl">البريد الإلكترونيّ</div>
              <div className="val" dir="ltr">info@balagha.tech</div>
            </div>
          </a>
          <a className="about-contact-card" href="https://www.balagha.tech"
             target="_blank" rel="noreferrer" data-testid="contact-website">
            <div className="ic">🌐</div>
            <div>
              <div className="lbl">رابط التطبيق</div>
              <div className="val" dir="ltr">www.balagha.tech</div>
            </div>
          </a>
          <div className="about-contact-card" data-testid="contact-partners">
            <div className="ic">🤝</div>
            <div>
              <div className="lbl">للشراكات المؤسّسيّة</div>
              <div className="val" dir="ltr">info@balagha.tech</div>
            </div>
          </div>
        </div>
      </Section>

      {/* ==================== Bottom CTAs ==================== */}
      <div className="about-cta">
        <button className="btn btn-gold" onClick={downloadPDF}
                data-testid="download-pdf-btn-bottom" disabled={downloading}>
          📄 تحميل ملف المشروع PDF
        </button>
        <button className="btn btn-primary" onClick={() => setPartnerOpen(true)}
                data-testid="request-partnership-btn-bottom">
          🤝 طلب شراكة
        </button>
        <button className="btn btn-ghost" onClick={() => navigate("/")}
                data-testid="about-back-btn">
          ابدأ تجربة بَلاغة ←
        </button>
      </div>

      <div className="about-footer muted" data-testid="about-footer">
        © {new Date().getFullYear()} بَلاغة — جميع الحقوق محفوظة. صُنع بحبٍّ
        لخدمة لغة الضاد.
      </div>

      <PartnershipModal open={partnerOpen} onClose={() => setPartnerOpen(false)} />
    </div>
  );
}
